import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DepartmentNav } from '../components/DepartmentNav';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { GraduationCap, Thermometer, Drop, Target, Calculator, Info, CheckCircle, Ruler, Warning, ArrowRight } from '@phosphor-icons/react';
import { SEO } from '../components/SEO';

export const LearnPage = () => {
  const { t } = useTranslation(['common', 'nav', 'learn']);
  const [selectedNozzle, setSelectedNozzle] = useState(null);

  // Nozzle data for calibration guide
  const nozzleTypes = [
    { id: 'xr', name: 'TeeJet XR (Extended Range)', gpm: 0.2, pressure: 40, pattern: 'Flat Fan', bestFor: 'General herbicides, liquid fertilizers' },
    { id: 'ai', name: 'TeeJet AI (Air Induction)', gpm: 0.3, pressure: 40, pattern: 'Flat Fan', bestFor: 'Drift-sensitive applications' },
    { id: 'tt', name: 'TeeJet TT (Turbo TeeJet)', gpm: 0.2, pressure: 30, pattern: 'Wide Flat Fan', bestFor: 'Contact herbicides' },
    { id: 'txvk', name: 'TeeJet TXVK (ConeJet)', gpm: 0.15, pressure: 40, pattern: 'Hollow Cone', bestFor: 'Fungicides, insecticides' },
    { id: 'red', name: 'Red Nozzle (04)', gpm: 0.4, pressure: 40, pattern: 'Flat Fan', bestFor: 'Higher volume applications' },
    { id: 'yellow', name: 'Yellow Nozzle (02)', gpm: 0.2, pressure: 40, pattern: 'Flat Fan', bestFor: 'Standard lawn applications' },
    { id: 'blue', name: 'Blue Nozzle (03)', gpm: 0.3, pressure: 40, pattern: 'Flat Fan', bestFor: 'Medium volume applications' },
  ];

  // Calibration formula: GPM × 5940 ÷ (Speed in MPH × Nozzle Spacing in inches) = GPA
  const calculateGPA = (gpm, speedMph, spacingInches) => {
    return ((gpm * 5940) / (speedMph * spacingInches)).toFixed(1);
  };

  return (
    <div data-testid="learn-page" className="min-h-screen bg-background">
      <SEO
        title="Learn & Guides"
        description="Free lawn care education: GDD explained, sprayer calibration guide, nozzle selection, and weed identification. Science-based lawn care knowledge for homeowners."
        keywords="lawn care guide, GDD explained, sprayer calibration, nozzle types, weed identification, growing degree days education, lawn care for beginners"
        path="/learn"
        imageAlt="TurfPulse Learn — Free lawn care education and calibration guides"
      />
      <DepartmentNav />
      
      <main className="max-w-5xl mx-auto p-6">
        <div className="mb-8">
          <h1 className="text-3xl font-bold flex items-center gap-3">
            <GraduationCap weight="duotone" size={32} className="text-primary" />
            Learn & Guides
          </h1>
          <p className="text-muted-foreground mt-2">
            Master the science behind smart lawn care
          </p>
        </div>

        <Tabs defaultValue="gdd" className="space-y-6">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="gdd" data-testid="tab-gdd">
              <Thermometer size={16} className="mr-2" />
              Understanding GDD
            </TabsTrigger>
            <TabsTrigger value="leveling" data-testid="tab-leveling">
              <Ruler size={16} className="mr-2" />
              Lawn Leveling
            </TabsTrigger>
            <TabsTrigger value="calibration" data-testid="tab-calibration">
              <Target size={16} className="mr-2" />
              Sprayer Calibration
            </TabsTrigger>
          </TabsList>

          {/* GDD Education Tab */}
          <TabsContent value="gdd" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Thermometer weight="duotone" size={24} className="text-primary" />
                  What Are Growing Degree Days (GDD)?
                </CardTitle>
                <CardDescription>
                  The science behind precision lawn care timing
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="bg-muted/50 rounded-lg p-4 border-l-4 border-primary">
                  <p className="font-medium">
                    Growing Degree Days measure accumulated heat over time — the biological clock your lawn actually runs on.
                  </p>
                </div>

                <div className="space-y-4">
                  <h3 className="font-semibold text-lg">Why Calendar Dates Don't Work</h3>
                  <p className="text-muted-foreground">
                    Traditional lawn care advice says "apply pre-emergent in March" or "fertilize every 6 weeks." But your grass doesn't have a calendar — it responds to temperature. A warm February can trigger growth that normally happens in April. A cool spring can delay everything by weeks.
                  </p>
                </div>

                <div className="space-y-4">
                  <h3 className="font-semibold text-lg">How GDD Works</h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="bg-background border rounded-lg p-4">
                      <h4 className="font-medium mb-2">The Formula</h4>
                      <code className="text-sm bg-muted px-2 py-1 rounded">
                        GDD = (High Temp + Low Temp) ÷ 2 - Base Temp
                      </code>
                      <p className="text-sm text-muted-foreground mt-2">
                        If High=80°F, Low=60°F, Base=50°F:<br />
                        GDD = (80 + 60) ÷ 2 - 50 = <strong>20 GDD</strong>
                      </p>
                    </div>
                    <div className="bg-background border rounded-lg p-4">
                      <h4 className="font-medium mb-2">Base Temperatures</h4>
                      <ul className="text-sm space-y-1">
                        <li><strong>Cool-Season Grass:</strong> 32°F base</li>
                        <li><strong>Warm-Season Grass:</strong> 50°F base</li>
                        <li><strong>Crabgrass germination:</strong> ~150 GDD (base 50)</li>
                        <li><strong>Grub activity:</strong> ~500-1000 GDD</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <h3 className="font-semibold text-lg">Practical Applications</h3>
                  <div className="grid gap-3">
                    <div className="flex items-start gap-3 p-3 bg-green-500/10 rounded-lg">
                      <CheckCircle weight="fill" size={20} className="text-green-600 mt-0.5" />
                      <div>
                        <p className="font-medium">Pre-Emergent Timing</p>
                        <p className="text-sm text-muted-foreground">Apply at 150-200 GDD (base 50) — before crabgrass germinates at soil temps of 55°F+</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3 p-3 bg-green-500/10 rounded-lg">
                      <CheckCircle weight="fill" size={20} className="text-green-600 mt-0.5" />
                      <div>
                        <p className="font-medium">PGR Applications</p>
                        <p className="text-sm text-muted-foreground">Reapply every 200-300 GDD depending on product — not "every 4 weeks"</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3 p-3 bg-green-500/10 rounded-lg">
                      <CheckCircle weight="fill" size={20} className="text-green-600 mt-0.5" />
                      <div>
                        <p className="font-medium">Grub Prevention</p>
                        <p className="text-sm text-muted-foreground">Target 500-1000 GDD when beetle larvae are actively feeding near the surface</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3 p-3 bg-green-500/10 rounded-lg">
                      <CheckCircle weight="fill" size={20} className="text-green-600 mt-0.5" />
                      <div>
                        <p className="font-medium">Fungicide Timing</p>
                        <p className="text-sm text-muted-foreground">Preventive fungicides work best applied before disease pressure peaks — GDD helps predict that window</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-primary/10 rounded-lg p-4">
                  <h4 className="font-semibold flex items-center gap-2">
                    <Info weight="fill" size={18} className="text-primary" />
                    Why TurfPulse Uses GDD
                  </h4>
                  <p className="text-sm mt-2">
                    Golf course superintendents and professional turf managers have used GDD for decades. TurfPulse brings that same precision to homeowners — tracking your local weather and calculating exactly when your lawn needs attention.
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Lawn Leveling Tab */}
          <TabsContent value="leveling" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Ruler weight="duotone" size={24} className="text-primary" />
                  Lawn Leveling: The Complete Guide
                </CardTitle>
                <CardDescription>
                  One of the most discussed topics on The Lawn Forum — here's what you need to know
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* What's the Goal */}
                <div className="space-y-3">
                  <h3 className="font-semibold text-lg">What's the Goal?</h3>
                  <div className="bg-muted/50 rounded-lg p-4 border-l-4 border-primary">
                    <p className="font-medium">
                      A level lawn isn't about aesthetics — it's about function. Bumpy yards cause scalping (uneven mowing), standing water, poor drainage, and unsafe footing.
                    </p>
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="p-4 bg-green-500/10 rounded-lg border border-green-500/20">
                      <h4 className="font-medium mb-2">Benefits of Leveling</h4>
                      <ul className="text-sm space-y-1.5 text-muted-foreground">
                        <li className="flex items-start gap-2"><CheckCircle weight="fill" size={16} className="text-green-600 mt-0.5 flex-shrink-0" /> Eliminates scalping — mower glides evenly across the surface</li>
                        <li className="flex items-start gap-2"><CheckCircle weight="fill" size={16} className="text-green-600 mt-0.5 flex-shrink-0" /> Improves drainage — water flows instead of pooling</li>
                        <li className="flex items-start gap-2"><CheckCircle weight="fill" size={16} className="text-green-600 mt-0.5 flex-shrink-0" /> Better ball roll (for the golf lawn nerds)</li>
                        <li className="flex items-start gap-2"><CheckCircle weight="fill" size={16} className="text-green-600 mt-0.5 flex-shrink-0" /> Cleaner reel mowing at low heights of cut</li>
                        <li className="flex items-start gap-2"><CheckCircle weight="fill" size={16} className="text-green-600 mt-0.5 flex-shrink-0" /> Safer play surface for kids and pets</li>
                      </ul>
                    </div>
                    <div className="p-4 bg-amber-500/10 rounded-lg border border-amber-500/20">
                      <h4 className="font-medium mb-2">Common Misconceptions</h4>
                      <ul className="text-sm space-y-1.5 text-muted-foreground">
                        <li className="flex items-start gap-2"><Warning weight="fill" size={16} className="text-amber-600 mt-0.5 flex-shrink-0" /> It's NOT a one-time fix — it takes multiple seasons</li>
                        <li className="flex items-start gap-2"><Warning weight="fill" size={16} className="text-amber-600 mt-0.5 flex-shrink-0" /> You can't bury grass more than 1/2 inch at a time</li>
                        <li className="flex items-start gap-2"><Warning weight="fill" size={16} className="text-amber-600 mt-0.5 flex-shrink-0" /> Sand alone won't fix major grade issues</li>
                        <li className="flex items-start gap-2"><Warning weight="fill" size={16} className="text-amber-600 mt-0.5 flex-shrink-0" /> Top dressing is leveling — they're the same process</li>
                      </ul>
                    </div>
                  </div>
                </div>

                {/* It's a Process */}
                <div className="space-y-3">
                  <h3 className="font-semibold text-lg">It's a Process, Not an Event</h3>
                  <div className="bg-primary/10 rounded-lg p-4">
                    <p className="text-sm">
                      <strong>Leveling is a multi-year commitment.</strong> Most homeowners need 2-4 leveling sessions over 1-3 seasons to achieve a truly flat lawn. Each session you can apply 1/4 to 1/2 inch of material without smothering the grass. If your yard has 2-3 inches of variation, that's multiple applications spread across multiple years.
                    </p>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    The lawn community (especially The Lawn Forum) will tell you: be patient. Trying to fix everything in one shot is the #1 mistake. You'll smother grass, create dead spots, and end up worse than when you started.
                  </p>
                </div>

                {/* Pre-Level Prep */}
                <div className="space-y-3">
                  <h3 className="font-semibold text-lg">Pre-Level Preparation: Cut It LOW</h3>
                  <div className="flex items-start gap-3 p-4 bg-red-500/10 rounded-lg border border-red-500/20">
                    <Warning weight="fill" size={24} className="text-red-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-medium text-sm">The grass MUST be cut as low as possible before leveling.</p>
                      <p className="text-sm text-muted-foreground mt-1">
                        Scalp the lawn down to 1/2 inch or lower. You need to see the soil and expose the low spots. If you level on top of tall grass, the material won't reach the soil surface and you'll have a bumpy layer of sand sitting on top of grass blades that will die underneath.
                      </p>
                    </div>
                  </div>
                  <div className="space-y-2 text-sm text-muted-foreground">
                    <p><strong>Step 1:</strong> Gradually lower your mowing height over 1-2 weeks (don't shock the grass by scalping in one pass)</p>
                    <p><strong>Step 2:</strong> Final scalp day — mow as low as your mower allows. Bag the clippings.</p>
                    <p><strong>Step 3:</strong> Use a dethatching rake or power rake if thatch is thick (&gt;1/2 inch). Sand needs to contact soil, not thatch.</p>
                  </div>
                </div>

                {/* What to Use */}
                <div className="space-y-3">
                  <h3 className="font-semibold text-lg">What Material to Use</h3>
                  <div className="grid md:grid-cols-3 gap-4">
                    <div className="p-4 bg-background border rounded-lg">
                      <h4 className="font-medium text-green-600 mb-2">Recommended: Sand</h4>
                      <p className="text-xs text-muted-foreground mb-2">Masonry sand or USGA-spec topdressing sand</p>
                      <ul className="text-xs space-y-1 text-muted-foreground">
                        <li>+ Doesn't compact over time</li>
                        <li>+ Improves drainage</li>
                        <li>+ Easy to spread and level</li>
                        <li>+ Gold standard for bermuda lawns</li>
                        <li>- Doesn't add nutrients</li>
                      </ul>
                    </div>
                    <div className="p-4 bg-background border rounded-lg">
                      <h4 className="font-medium text-amber-600 mb-2">Alternative: Sand + Compost Mix</h4>
                      <p className="text-xs text-muted-foreground mb-2">70/30 or 80/20 sand-to-compost ratio</p>
                      <ul className="text-xs space-y-1 text-muted-foreground">
                        <li>+ Adds organic matter</li>
                        <li>+ Helps with nutrient retention</li>
                        <li>+ Good for soil building</li>
                        <li>- Compost can contain weed seeds</li>
                        <li>- Harder to screen for consistency</li>
                      </ul>
                    </div>
                    <div className="p-4 bg-background border rounded-lg">
                      <h4 className="font-medium text-red-600 mb-2">Avoid: Topsoil Alone</h4>
                      <p className="text-xs text-muted-foreground mb-2">Topsoil is NOT leveling material</p>
                      <ul className="text-xs space-y-1 text-muted-foreground">
                        <li>- Compacts heavily when wet</li>
                        <li>- Creates drainage layers</li>
                        <li>- Inconsistent texture</li>
                        <li>- Often full of weed seeds</li>
                        <li>- Creates more problems than it solves</li>
                      </ul>
                    </div>
                  </div>
                </div>

                {/* First-Time Recommendation */}
                <div className="space-y-3">
                  <h3 className="font-semibold text-lg">First-Time Leveling: Start Small</h3>
                  <div className="bg-blue-500/10 rounded-lg p-4 border border-blue-500/20">
                    <h4 className="font-medium mb-2">Recommended Approach for Beginners</h4>
                    <ol className="text-sm space-y-2 text-muted-foreground">
                      <li><strong>1. Start with a small area</strong> — Pick the worst 500-1000 sq ft section first. Learn the process before doing the whole yard.</li>
                      <li><strong>2. Order the right amount</strong> — 1 cubic yard of sand covers roughly 1,000 sq ft at 1/4 inch depth. For a half-inch application, double it.</li>
                      <li><strong>3. Use a leveling rake (lute)</strong> — A 36" or 42" leveling rake is essential. Don't try to level with a regular garden rake. The Accuform AccuLevel and the LevelLawn rake are popular choices.</li>
                      <li><strong>4. Drag with a drag mat</strong> — After spreading with the lute, pull a drag mat or piece of chain-link fence behind your mower to work sand into the canopy.</li>
                      <li><strong>5. Water it in</strong> — After leveling, irrigate to settle the sand. This also helps you see what's still uneven.</li>
                      <li><strong>6. Repeat in 4-6 weeks</strong> — Once the grass grows through, you can do another pass on the same area or move to the next section.</li>
                    </ol>
                  </div>
                  <p className="text-xs text-muted-foreground italic">
                    Pro tip from The Lawn Forum: "If you can still see blade tips after applying, you're doing it right. If you can't see any grass, you applied too much."
                  </p>
                </div>

                {/* Timing */}
                <div className="space-y-3">
                  <h3 className="font-semibold text-lg">Timing: When to Level</h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="p-4 bg-green-500/10 rounded-lg border border-green-500/20">
                      <h4 className="font-medium text-green-700 dark:text-green-400 mb-2">Best Window (Warm-Season)</h4>
                      <ul className="text-sm space-y-1 text-muted-foreground">
                        <li className="flex items-start gap-2"><CheckCircle weight="fill" size={16} className="text-green-600 mt-0.5 flex-shrink-0" /> <strong>April through July</strong> — Peak growing season</li>
                        <li className="flex items-start gap-2"><CheckCircle weight="fill" size={16} className="text-green-600 mt-0.5 flex-shrink-0" /> Bermuda recovers fastest when actively growing</li>
                        <li className="flex items-start gap-2"><CheckCircle weight="fill" size={16} className="text-green-600 mt-0.5 flex-shrink-0" /> Soil temps consistently above 70°F</li>
                        <li className="flex items-start gap-2"><CheckCircle weight="fill" size={16} className="text-green-600 mt-0.5 flex-shrink-0" /> Ideal: 2-3 weeks after full green-up</li>
                      </ul>
                    </div>
                    <div className="p-4 bg-red-500/10 rounded-lg border border-red-500/20">
                      <h4 className="font-medium text-red-700 dark:text-red-400 mb-2">Latest You Should Level</h4>
                      <ul className="text-sm space-y-1 text-muted-foreground">
                        <li className="flex items-start gap-2"><Warning weight="fill" size={16} className="text-red-600 mt-0.5 flex-shrink-0" /> <strong>No later than mid-August</strong> (for warm-season)</li>
                        <li className="flex items-start gap-2"><Warning weight="fill" size={16} className="text-red-600 mt-0.5 flex-shrink-0" /> Grass needs 6-8 weeks to recover before dormancy</li>
                        <li className="flex items-start gap-2"><Warning weight="fill" size={16} className="text-red-600 mt-0.5 flex-shrink-0" /> Late leveling = thin, weak turf going into winter</li>
                        <li className="flex items-start gap-2"><Warning weight="fill" size={16} className="text-red-600 mt-0.5 flex-shrink-0" /> Fall pre-emergent conflicts with sand application</li>
                      </ul>
                    </div>
                  </div>
                  <div className="p-4 bg-muted/50 rounded-lg">
                    <h4 className="font-medium mb-2">Cool-Season Lawns</h4>
                    <p className="text-sm text-muted-foreground">
                      For cool-season grasses (tall fescue, Kentucky bluegrass), the best leveling window is <strong>early fall (September-October)</strong> when the grass is actively growing and temps are moderate. Spring is a secondary option but avoid leveling when crabgrass pre-emergent is down.
                    </p>
                  </div>
                </div>

                {/* How Much Sand */}
                <div className="space-y-3">
                  <h3 className="font-semibold text-lg">How Much Sand Do I Need?</h3>
                  <div className="bg-background border rounded-lg p-4">
                    <div className="grid grid-cols-3 gap-4 text-center text-sm">
                      <div className="p-3 bg-muted/50 rounded">
                        <p className="font-bold text-lg">1 yd³</p>
                        <p className="text-muted-foreground">covers ~1,000 sq ft<br />at 1/4" depth</p>
                      </div>
                      <div className="p-3 bg-muted/50 rounded">
                        <p className="font-bold text-lg">2 yd³</p>
                        <p className="text-muted-foreground">covers ~1,000 sq ft<br />at 1/2" depth</p>
                      </div>
                      <div className="p-3 bg-primary/10 rounded border border-primary/30">
                        <p className="font-bold text-lg">~$40-60</p>
                        <p className="text-muted-foreground">per cubic yard<br />(bulk delivery)</p>
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground mt-3 text-center">
                      For a 5,000 sq ft lawn at 1/4" depth, budget ~5 cubic yards (~$200-300 delivered).
                      Most areas have landscape supply yards that deliver in bulk.
                    </p>
                  </div>
                </div>

                {/* Forum Reference */}
                <div className="bg-muted/30 rounded-lg p-4">
                  <h4 className="font-semibold mb-2 flex items-center gap-2">
                    <Info weight="fill" size={18} className="text-primary" />
                    Community Resources
                  </h4>
                  <p className="text-sm text-muted-foreground">
                    Lawn leveling is one of the most discussed topics in the lawn care community. For deep-dive discussions, progress photos, and firsthand experiences, check out <strong>The Lawn Forum</strong> (thelawnforum.com) — search for "leveling" or "top dressing." Hundreds of members have documented their multi-year leveling journeys with before/after photos.
                  </p>
                </div>

                {/* Partners CTA */}
                <div className="bg-primary/10 rounded-lg p-5 border border-primary/20" data-testid="leveling-partners-cta">
                  <h4 className="font-semibold mb-2 flex items-center gap-2">
                    <ArrowRight weight="bold" size={18} className="text-primary" />
                    Need a Pro? Check the Partners Tab
                  </h4>
                  <p className="text-sm text-muted-foreground mb-3">
                    Not ready to DIY? Our <strong>Partners Directory</strong> includes qualified lawn leveling contractors in your area. Many of them share leveling tips, time-lapses, and tutorials on their Facebook and YouTube channels — great for learning the technique before hiring or doing it yourself.
                  </p>
                  <Button
                    variant="default"
                    size="sm"
                    onClick={() => window.location.href = '/partners'}
                    data-testid="leveling-go-to-partners"
                  >
                    Browse Contractors
                    <ArrowRight weight="bold" size={16} className="ml-2" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Sprayer Calibration Tab */}
          <TabsContent value="calibration" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Target weight="duotone" size={24} className="text-primary" />
                  Sprayer Calibration Guide
                </CardTitle>
                <CardDescription>
                  Accurate application rates save money and protect your lawn
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="bg-muted/50 rounded-lg p-4 border-l-4 border-primary">
                  <p className="font-medium">
                    Proper calibration ensures you apply the right amount — too little wastes product, too much can damage turf.
                  </p>
                </div>

                {/* Step by Step Guide */}
                <div className="space-y-4">
                  <h3 className="font-semibold text-lg">Step-by-Step Calibration</h3>
                  
                  <div className="space-y-3">
                    <div className="flex gap-4 p-4 bg-background border rounded-lg">
                      <div className="bg-primary text-primary-foreground rounded-full w-8 h-8 flex items-center justify-center font-bold shrink-0">1</div>
                      <div>
                        <h4 className="font-medium">Measure Your Walking Speed</h4>
                        <p className="text-sm text-muted-foreground">Mark a 100-foot course. Walk it at your normal spraying pace while timing yourself. Repeat 3 times and average.</p>
                        <p className="text-sm mt-1"><strong>Example:</strong> 100 ft in 30 seconds = 2.27 MPH</p>
                      </div>
                    </div>

                    <div className="flex gap-4 p-4 bg-background border rounded-lg">
                      <div className="bg-primary text-primary-foreground rounded-full w-8 h-8 flex items-center justify-center font-bold shrink-0">2</div>
                      <div>
                        <h4 className="font-medium">Determine Nozzle Output (GPM)</h4>
                        <p className="text-sm text-muted-foreground">Spray into a container for exactly 1 minute at your operating pressure. Measure in ounces, divide by 128 for gallons.</p>
                        <p className="text-sm mt-1"><strong>Example:</strong> 26 oz ÷ 128 = 0.2 GPM</p>
                      </div>
                    </div>

                    <div className="flex gap-4 p-4 bg-background border rounded-lg">
                      <div className="bg-primary text-primary-foreground rounded-full w-8 h-8 flex items-center justify-center font-bold shrink-0">3</div>
                      <div>
                        <h4 className="font-medium">Calculate Gallons Per Acre (GPA)</h4>
                        <p className="text-sm text-muted-foreground">Use the formula below with your nozzle spacing (typically 20" for handheld wands).</p>
                        <code className="text-sm bg-muted px-2 py-1 rounded block mt-2">
                          GPA = (GPM × 5940) ÷ (MPH × Spacing in inches)
                        </code>
                      </div>
                    </div>

                    <div className="flex gap-4 p-4 bg-background border rounded-lg">
                      <div className="bg-primary text-primary-foreground rounded-full w-8 h-8 flex items-center justify-center font-bold shrink-0">4</div>
                      <div>
                        <h4 className="font-medium">Adjust Product Rate</h4>
                        <p className="text-sm text-muted-foreground">Most lawn products label rates in oz/1000 sq ft. Convert your GPA to match.</p>
                        <p className="text-sm mt-1"><strong>Quick math:</strong> 1 acre = 43,560 sq ft ≈ 44 units of 1000 sq ft</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Nozzle Reference */}
                <div className="space-y-4">
                  <h3 className="font-semibold text-lg">Common Nozzle Reference</h3>
                  <div className="grid md:grid-cols-2 gap-3">
                    {nozzleTypes.map((nozzle) => (
                      <div 
                        key={nozzle.id}
                        onClick={() => setSelectedNozzle(selectedNozzle?.id === nozzle.id ? null : nozzle)}
                        className={`p-4 border rounded-lg cursor-pointer transition-all ${
                          selectedNozzle?.id === nozzle.id ? 'border-primary bg-primary/5' : 'hover:border-muted-foreground/50'
                        }`}
                      >
                        <h4 className="font-medium">{nozzle.name}</h4>
                        <div className="flex gap-4 mt-2 text-sm text-muted-foreground">
                          <span>{nozzle.gpm} GPM</span>
                          <span>{nozzle.pressure} PSI</span>
                          <span>{nozzle.pattern}</span>
                        </div>
                        {selectedNozzle?.id === nozzle.id && (
                          <div className="mt-3 pt-3 border-t text-sm">
                            <p><strong>Best for:</strong> {nozzle.bestFor}</p>
                            <p className="mt-2">
                              <strong>At 3 MPH, 20" spacing:</strong>{' '}
                              {calculateGPA(nozzle.gpm, 3, 20)} GPA
                            </p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Quick Tips */}
                <div className="space-y-4">
                  <h3 className="font-semibold text-lg">Pro Tips</h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="p-4 bg-yellow-500/10 rounded-lg border border-yellow-500/20">
                      <h4 className="font-medium flex items-center gap-2">
                        <Drop weight="fill" size={18} className="text-yellow-600" />
                        Drift Control
                      </h4>
                      <ul className="text-sm mt-2 space-y-1 text-muted-foreground">
                        <li>• Use AI (Air Induction) nozzles in windy conditions</li>
                        <li>• Keep pressure at 30-40 PSI max</li>
                        <li>• Spray when wind is under 10 MPH</li>
                        <li>• Larger droplets = less drift</li>
                      </ul>
                    </div>
                    <div className="p-4 bg-blue-500/10 rounded-lg border border-blue-500/20">
                      <h4 className="font-medium flex items-center gap-2">
                        <Calculator weight="fill" size={18} className="text-blue-600" />
                        Coverage Check
                      </h4>
                      <ul className="text-sm mt-2 space-y-1 text-muted-foreground">
                        <li>• Add blue dye to your tank to visualize coverage</li>
                        <li>• Overlap passes by 50% for even distribution</li>
                        <li>• Walk the same speed consistently</li>
                        <li>• Re-calibrate when changing nozzles</li>
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Backpack Sprayer Specifics */}
                <div className="bg-muted/30 rounded-lg p-4">
                  <h4 className="font-semibold mb-2">Backpack Sprayer Quick Calibration</h4>
                  <p className="text-sm text-muted-foreground mb-3">
                    For most 4-gallon backpack sprayers with a standard wand:
                  </p>
                  <ol className="text-sm space-y-2">
                    <li><strong>1.</strong> Fill tank with plain water</li>
                    <li><strong>2.</strong> Spray a measured 1,000 sq ft area at normal pace</li>
                    <li><strong>3.</strong> Measure water used (refill and compare)</li>
                    <li><strong>4.</strong> That's your oz/1,000 sq ft application rate</li>
                    <li><strong>5.</strong> Adjust pace or nozzle to match product label rate</li>
                  </ol>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};
